/**
 * pdfHelpers.js — Primitives et thème pour la génération de rapports PDF
 *
 * Palette : #1B77F1 (bleu primaire)
 * Font : Helvetica (built-in jsPDF)
 *
 * Module feuille (cap 500 lignes). Les tableaux de données vivent dans
 * pdfTables.js, les sections de contenu dans pdfSections.js — tous deux
 * importent BRAND (et checkPageBreak) depuis ce fichier.
 *
 * i18n : module non-React → instance singleton i18n (pattern util→i18n
 * acté roleLabel/mapUtils), résolution à la génération (langue UI courante).
 */
import i18n from '../i18n';
import { formatDateLocale } from '../utils/formatters';


// Couleurs de la marque
export const BRAND = {
  blue1: [27, 119, 241],    // #1B77F1
  dark: [44, 62, 80],       // #2C3E50
  gray: [107, 123, 141],    // #4A5568
  lightGray: [245, 245, 245],
  white: [255, 255, 255],
  green: [16, 185, 129],
  orange: [245, 158, 11],
  red: [239, 68, 68],
};

/**
 * Dessine l'en-tête du rapport avec dégradé bleu simulé
 * @param {string} [title] titre du document, sous le logo (PDF de comparaison) ;
 *   absent : en-tête strictement identique au PDF de session.
 */
export function drawHeader(pdf, pageWidth, title) {
  const headerHeight = 42;

  // Bandeau bleu uni
  pdf.setFillColor(...BRAND.blue1);
  pdf.rect(0, 0, pageWidth, headerHeight, 'F');

  // Logo texte blanc — pas de dépendance image
  pdf.setTextColor(...BRAND.white);
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.text('EkynAir', 14, headerHeight / 2 + 4);
  if (title) {
    pdf.setFontSize(11);
    pdf.text(title, 14, headerHeight / 2 + 13);
  }

  // Sous-titre + date à droite
  pdf.setTextColor(...BRAND.white);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(i18n.t('pdf.header.subtitle'), pageWidth - 15, 16, { align: 'right' });
  const now     = new Date();
  // Date/heure de génération : valeurs localisées (mois-long FR byte-for-byte / EN corrigé ;
  // heure colon identique FR/EN). Le connecteur « à »/« at » vit dans le template i18n.
  const dateStr  = formatDateLocale(now, { day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr  = formatDateLocale(now, { hour: '2-digit', minute: '2-digit' });
  pdf.text(i18n.t('pdf.header.generatedAt', { date: dateStr, time: timeStr }), pageWidth - 15, 25, { align: 'right' });
  pdf.text('ekynair.app', pageWidth - 15, 33, { align: 'right' });

  return headerHeight + 8;
}

/**
 * Dessine un titre de section avec ligne bleue
 */
export function drawSectionTitle(pdf, text, yPos, margin) {
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...BRAND.blue1);
  pdf.text(text, margin, yPos);

  // Ligne sous le titre
  pdf.setDrawColor(...BRAND.blue1);
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPos + 2, margin + pdf.getTextWidth(text), yPos + 2);

  pdf.setTextColor(...BRAND.dark);
  return yPos + 10;
}

/**
 * Dessine un tableau de données structuré
 * @param {Array} rows - [{label, value, color?}]
 */
export function drawInfoTable(pdf, rows, yPos, margin, colWidth) {
  pdf.setFontSize(10);
  const rowHeight = 7;

  rows.forEach((row, idx) => {
    // Fond alternée
    if (idx % 2 === 0) {
      pdf.setFillColor(...BRAND.lightGray);
      pdf.rect(margin, yPos - 4.5, colWidth, rowHeight, 'F');
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...BRAND.dark);
    pdf.text(row.label, margin + 3, yPos);

    pdf.setFont('helvetica', 'normal');
    if (row.color) {
      pdf.setTextColor(...row.color);
    } else {
      pdf.setTextColor(...BRAND.dark);
    }
    pdf.text(row.value, margin + colWidth * 0.45, yPos);

    yPos += rowHeight;
  });

  pdf.setTextColor(...BRAND.dark);
  return yPos + 3;
}

/**
 * Vérifie s'il faut un saut de page et le fait si nécessaire
 */
export function checkPageBreak(pdf, yPos, needed, margin, pageHeight) {
  if (yPos + needed > pageHeight - 20) {
    pdf.addPage();
    return margin + 5;
  }
  return yPos;
}

// JPEG 0.82 / fond blanc obligatoire / cf. §18.6 spec
/**
 * Composite un canvas source sur un canvas intermédiaire à fond blanc,
 * puis encode en JPEG. JPEG ne supporte pas la transparence — sans
 * composite, les zones transparentes du canvas Chart.js / html2canvas
 * apparaissent en noir dans le PDF.
 *
 * Extrait V1.1-X (sprint V1.2-cleanup, 17/5/2026) : factorisation du
 * pattern dupliqué entre `PDFExport.jsx` (helper interne addChartToPDF)
 * et le générateur PDF véto (consommé depuis `vetpro/pdf/vetPdfClinical.js`
 * Sprint A pré-audit Van Erck — précédemment `vetPdfGenerator.js`, supprimé
 * D114). Le PDF map (PDFExport.jsx:479) utilise un pattern DIFFÉRENT
 * (html2canvas backgroundColor: '#fff' natif, pas de composite) et n'est
 * PAS concerné par ce helper.
 *
 * @param {HTMLCanvasElement|HTMLImageElement} sourceCanvas — canvas avec
 *   transparence éventuelle (Chart.js ou html2canvas), ou photo (10.5.300)
 * @param {number} [quality=0.82] — qualité JPEG (0..1). Default 0.82,
 *   valeur utilisée par les 2 call-sites historiques.
 * @param {number} [width]  — largeur de sortie (défaut : celle de la source)
 * @param {number} [height] — hauteur de sortie (défaut : celle de la source)
 * @returns {string} data URL base64 JPEG
 */
export function compositeOnWhite(sourceCanvas, quality = 0.82,
  width = sourceCanvas.width, height = sourceCanvas.height) {
  const flat = document.createElement('canvas');
  flat.width  = width;
  flat.height = height;
  const ctx = flat.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, flat.width, flat.height);
  ctx.drawImage(sourceCanvas, 0, 0, width, height);
  return flat.toDataURL('image/jpeg', quality);
}

/**
 * Photo → data URL JPEG réduite, pour addImage (D225, 10.5.300).
 *
 * Pourquoi : passé tel quel, un HTMLImageElement est relu par jsPDF via un
 * XHR synchrone sur son blob URL, que la CSP de prod bloque (connect-src sans
 * blob:) : aucune photo n'est jamais sortie dans le PDF. Redessiner sur canvas
 * évite ce XHR et réduit au passage les photos de 24 Mpx (~7 Mo chacune,
 * insérées brutes) à une taille de vignette. Le ratio est conservé, le grand
 * côté plafonné à maxPx, jamais agrandi ; fond blanc pour PNG/WebP transparents.
 *
 * @param {HTMLImageElement} img — image chargée (naturalWidth/naturalHeight)
 * @param {number} [maxPx=1600] — grand côté maximal en pixels
 * @param {number} [quality=0.82] — qualité JPEG
 * @returns {{ dataUrl: string, width: number, height: number }}
 */
export function imageToJpegDataUrl(img, maxPx = 1600, quality = 0.82) {
  const scale  = Math.min(1, maxPx / Math.max(img.naturalWidth, img.naturalHeight));
  const width  = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  return { dataUrl: compositeOnWhite(img, quality, width, height), width, height };
}

/**
 * Nom de fichier du rapport PDF owner, langue-dépendant (même mécanique que le
 * PDF clinique B2-4c : slug via i18n.t hors React). Structure PRÉSERVÉE
 * « EkynAir-<slug>-<Cheval>[-<Titre>].pdf » ; seul le slug bascule
 * (FR « Rapport » byte-for-byte / EN « report »). Cheval/Titre = données user,
 * jamais traduites, seulement assainies (charset fichier).
 *
 * @param {string} horseName - nom du cheval (donnée user)
 * @param {string} title     - titre de session (donnée user, optionnel)
 * @returns {string} nom de fichier .pdf
 */
// suffix : complément facultatif (période analysée, pdfPeriod), absent : nom inchangé.
export function buildPdfFilename(horseName, title, suffix) {
  const slug = i18n.t('pdf.filenameSlug');
  // Assainissement identique à l'inline d'origine (byte-for-byte, y compris cas limites).
  const safeHorse = (horseName || 'Cheval').replace(/[^a-zA-Z0-9À-ÿ]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const safeTitle = (title || '').replace(/[^a-zA-Z0-9À-ÿ]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const base = safeTitle
    ? `EkynAir-${slug}-${safeHorse}-${safeTitle}`
    : `EkynAir-${slug}-${safeHorse}`;
  return suffix ? `${base}-${suffix}.pdf` : `${base}.pdf`;
}
